import { useState, useEffect, useCallback, Fragment } from 'react';
import { Search, RefreshCw, UserPlus, Trash2, UserCheck, Mail, Plus, X, ChevronDown, ChevronRight, Edit2, Check, Server } from 'lucide-react';
import {
  listCustomers, createCustomer, updateCustomer, deleteCustomer,
  listCustomerEmails, addCustomerEmail, removeCustomerEmail,
  listDids, syncDids, assignDids,
  listVoiceUris, syncVoiceUris, createVoiceUri, updateVoiceUri, deleteVoiceUri,
  listSbcs, createSbc, updateSbc, deleteSbc, pollSbc,
  listVoiceRoutingPolicies,
  getSbcDialPlan, getSbcDialPlanRaw, getSbcConfigFull, addSbcDialPlanEntry, deleteSbcDialPlanEntry,
  getSbcPollConfig, setSbcPollConfig, getPowershellStatus, installPowershellModule,
  getCdrRetention, setCdrRetention, pruneCdrsNow,
  getCustomerModules, setCustomerModules,
  getCustomerVoxbone, setCustomerVoxbone, testCustomerVoxbone,
  getChangeLog, restoreChange,
  listCarriers, createCarrier, updateCarrier, deleteCarrier, setCarrierSbcs, inferCarriers,
} from '../services/portalApi';
import { CUSTOMER_MODULES, CUSTOMER_MODULE_IDS } from '../modules';

// ── Change Log tab (admin audit + selective restore) ─────────────────────────
function ChangeLogTab({ onFlash }) {
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await getChangeLog(300); setRows(d.changes || []); }
    catch (e) { onFlash('error', e.message); }
    finally { setLoading(false); }
  }, [onFlash]);
  useEffect(() => { load(); }, [load]);

  async function doRestore(r) {
    if (!window.confirm(`Restore "${r.label}" to its previous setting?`)) return;
    setRestoring(r.id);
    try {
      await restoreChange(r.id);
      onFlash('success', 'Setting restored');
      await load();
    } catch (e) { onFlash('error', e.message); }
    finally { setRestoring(null); }
  }

  const fmt = (ts) => { try { return new Date(ts.replace(' ', 'T') + 'Z').toLocaleString(); } catch { return ts; } };
  const actionColor = (a) => a === 'restore' ? 'var(--purple)' : a === 'create' ? 'var(--ok)' : a === 'delete' ? 'var(--err)' : 'var(--info)';

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Change Log</div>
          <div className="card-sub">Every tracked setting change, who made it, and the before/after. Kept 14 days.</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={13} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined}/> Refresh
        </button>
      </div>
      <div className="card-body" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: 40 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: 40 }}>No changes recorded yet.</div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>When</th>
                <th style={{ textAlign: 'left' }}>Who</th>
                <th style={{ textAlign: 'left' }}>Change</th>
                <th style={{ textAlign: 'left' }}>Action</th>
                <th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <Fragment key={r.id}>
                  <tr>
                    <td style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmt(r.createdAt)}</td>
                    <td style={{ fontSize: 12 }}>{r.user}</td>
                    <td style={{ fontSize: 13 }}>
                      <span onClick={() => setExpanded(expanded === r.id ? null : r.id)} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {expanded === r.id ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
                        {r.label}
                      </span>
                      {r.restoredAt && <span className="badge badge-gray" style={{ marginLeft: 6 }}>restored</span>}
                    </td>
                    <td><span className="badge" style={{ color: actionColor(r.action), textTransform: 'capitalize' }}>{r.action}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      {r.restorable && !r.restoredAt && (
                        <button className="btn btn-secondary btn-xs" disabled={restoring === r.id} onClick={() => doRestore(r)}>
                          {restoring === r.id ? 'Restoring…' : 'Restore'}
                        </button>
                      )}
                      {!r.restorable && <span style={{ fontSize: 11, color: 'var(--muted-3)' }} title="Audit only — before/after recorded, not auto-restorable">audit only</span>}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--bg)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '10px 14px' }}>
                          <div>
                            <div style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--muted-3)', marginBottom: 4 }}>Before</div>
                            <pre style={{ fontSize: 11, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--muted)' }}>{r.before ? JSON.stringify(r.before, null, 2) : '—'}</pre>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--muted-3)', marginBottom: 4 }}>After</div>
                            <pre style={{ fontSize: 11, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--text-2)' }}>{r.after ? JSON.stringify(r.after, null, 2) : '—'}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── System settings tab ───────────────────────────────────────────────────────
function SystemTab({ onFlash }) {
  const [days, setDays]       = useState('');
  const [cdrCount, setCount]  = useState(null);
  const [saving, setSaving]   = useState(false);
  const [pruning, setPruning] = useState(false);

  const load = useCallback(() => {
    getCdrRetention()
      .then(d => { setDays(String(d.days)); setCount(d.cdrCount); })
      .catch(e => onFlash('error', e.message));
  }, [onFlash]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    const n = Number(days);
    if (!Number.isFinite(n) || n < 0) return onFlash('warn', 'Enter 0 or a positive number of days');
    setSaving(true);
    try {
      const d = await setCdrRetention(Math.round(n));
      setDays(String(d.days));
      onFlash('success', d.days === 0 ? 'CDR retention set to keep forever' : `CDR retention set to ${d.days} days`);
    } catch (e) { onFlash('error', e.message); }
    finally { setSaving(false); }
  }

  async function prune() {
    if (!window.confirm('Delete all CDRs older than the retention window now? This cannot be undone.')) return;
    setPruning(true);
    try {
      const r = await pruneCdrsNow();
      setCount(r.cdrCount);
      onFlash('success', 'Retention sweep complete');
    } catch (e) { onFlash('error', e.message); }
    finally { setPruning(false); }
  }

  return (
    <div className="card" style={{ maxWidth: 560 }}>
      <div className="card-header"><div className="card-title">Call Record (CDR) Retention</div></div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          Call records older than this are deleted automatically each day. Set to <strong>0</strong> to keep records forever.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ fontSize: 13, color: 'var(--text-2)' }}>Keep CDRs for</label>
          <input
            type="number" min="0" max="3650" value={days}
            onChange={e => setDays(e.target.value)}
            style={{ width: 90 }}
          />
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>days</span>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        {cdrCount != null && (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            Currently storing <strong style={{ color: 'var(--text-2)' }}>{cdrCount.toLocaleString()}</strong> call record{cdrCount === 1 ? '' : 's'}.
          </div>
        )}
        <div>
          <button className="btn btn-secondary btn-sm" onClick={prune} disabled={pruning}>
            {pruning ? 'Pruning…' : 'Run retention sweep now'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Inline email management panel ────────────────────────────────────────────

function EmailsPanel({ customerId, onFlash }) {
  const [emails,   setEmails]   = useState(null); // null = not yet loaded
  const [newEmail, setNewEmail] = useState('');
  const [newPwd,   setNewPwd]   = useState('');
  const [adding,   setAdding]   = useState(false);

  useEffect(() => {
    listCustomerEmails(customerId)
      .then(d => setEmails(d.emails || []))
      .catch(e => onFlash('error', e.message));
  }, [customerId, onFlash]);

  async function handleAdd(e) {
    e.preventDefault();
    setAdding(true);
    try {
      await addCustomerEmail(customerId, newEmail, newPwd);
      setNewEmail('');
      setNewPwd('');
      const d = await listCustomerEmails(customerId);
      setEmails(d.emails || []);
      onFlash('success', `Added ${newEmail}`);
    } catch (err) {
      onFlash('error', err.message);
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(emailId, email) {
    try {
      await removeCustomerEmail(customerId, emailId);
      setEmails(prev => prev.filter(e => e.id !== emailId));
      onFlash('success', `Removed ${email}`);
    } catch (err) {
      onFlash('error', err.message);
    }
  }

  if (emails === null) return <div style={{ color: 'var(--muted-3)', fontSize: 12, padding: '8px 0' }}>Loading…</div>;

  return (
    <div style={{ padding: '14px 20px 14px 56px', background: '#0f111a', borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, color: 'var(--muted-3)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>
        Additional Login Emails
      </div>

      {emails.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--muted-3)', marginBottom: 10 }}>No additional emails added yet.</div>
      )}

      {emails.map(em => (
        <div key={em.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Mail size={12} color="var(--muted-3)" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--muted)', flex: 1 }}>{em.email}</span>
          <span style={{ fontSize: 11, color: 'var(--muted-3)' }}>added {em.created_at?.slice(0, 10)}</span>
          <button
            className="btn btn-secondary btn-xs"
            style={{ color: 'var(--err)' }}
            onClick={() => handleRemove(em.id, em.email)}
          >
            <X size={10} /> Remove
          </button>
        </div>
      ))}

      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input
          className="form-input"
          type="email"
          placeholder="Additional email address"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          style={{ flex: 1, fontSize: 12 }}
          required
        />
        <input
          className="form-input"
          type="password"
          placeholder="Password"
          value={newPwd}
          onChange={e => setNewPwd(e.target.value)}
          style={{ width: 140, fontSize: 12 }}
          required
        />
        <button className="btn btn-secondary btn-sm" type="submit" disabled={adding}>
          <Plus size={12} /> {adding ? 'Adding…' : 'Add Email'}
        </button>
      </form>
    </div>
  );
}

// ── Voice URI management tab ─────────────────────────────────────────────────

const PROTOCOLS = ['SIP', 'SIP_DTMF2833', 'SIP_DTMF_INBAND', 'TEL'];

// Multi-select list of SBCs for a Voice URI (one or more SBCs per carrier).
function SbcPicker({ sbcs, selected, onChange }) {
  const sel = selected || [];
  if (!sbcs || sbcs.length === 0) return <span style={{ fontSize: 11, color: 'var(--muted-3)' }}>No SBCs</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 84, overflowY: 'auto' }}>
      {sbcs.map(s => {
        const checked = sel.includes(s.id);
        return (
          <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={e => onChange(e.target.checked ? [...sel, s.id] : sel.filter(x => x !== s.id))}
            />
            {s.name}
          </label>
        );
      })}
    </div>
  );
}

function VoiceUrisTab({ onFlash }) {
  const [uris,      setUris]     = useState([]);
  const [sbcs,      setSbcs]     = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [syncing,   setSyncing]  = useState(false);
  const [syncMsg,   setSyncMsg]  = useState(null);
  const [editId,    setEditId]   = useState(null);
  const [editDraft, setEditDraft]= useState({});
  const [addMode,   setAddMode]  = useState(false);
  const [addDraft,  setAddDraft] = useState({ description: '', uri: '', protocol: 'SIP', sbcIds: [] });
  const [saving,    setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([listVoiceUris(), listSbcs()]);
      setUris(d.voiceUris || []);
      setSbcs(s.sbcs || []);
    } catch (err) {
      onFlash('error', err.message);
    } finally {
      setLoading(false);
    }
  }, [onFlash]);

  useEffect(() => { load(); }, [load]);

  async function handleSync() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const r = await syncVoiceUris();
      setSyncMsg(`${r.count} synced`);
      onFlash('success', `Synced ${r.count} Voice URIs from Voxbone`);
      await load();
    } catch (err) {
      onFlash('error', err.message);
    } finally {
      setSyncing(false);
    }
  }

  function startEdit(u) {
    setEditId(u.id);
    setEditDraft({ description: u.description, uri: u.uri, protocol: u.protocol, sbcIds: u.sbcIds || [] });
    setAddMode(false);
  }

  async function handleSave(id) {
    setSaving(true);
    try {
      const r = await updateVoiceUri(id, editDraft);
      setEditId(null);
      await load();   // refresh so the SBC names column reflects the new links
      onFlash('success', r.voxboneSynced ? 'Voice URI updated (synced to Voxbone)' : 'Voice URI updated (local only)');
    } catch (err) {
      onFlash('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, description) {
    if (!window.confirm(`Delete "${description || 'this Voice URI'}"?`)) return;
    try {
      const r = await deleteVoiceUri(id);
      setUris(prev => prev.filter(u => u.id !== id));
      onFlash('success', r.voxboneSynced ? 'Voice URI deleted (removed from Voxbone)' : 'Voice URI deleted (local only)');
    } catch (err) {
      onFlash('error', err.message);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await createVoiceUri({ ...addDraft });
      setAddDraft({ description: '', uri: '', protocol: 'SIP', sbcIds: [] });
      setAddMode(false);
      await load();
      onFlash('success', r.voxboneSynced ? 'Voice URI added (synced to Voxbone)' : 'Voice URI added (local only)');
    } catch (err) {
      onFlash('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = {
    width: '100%', fontSize: 12, padding: '3px 8px',
    background: 'var(--bg)', border: '1px solid #3a3f5c', borderRadius: 4, color: 'var(--text-2)',
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Voice URIs</div>
          <div className="card-sub">Routing destinations from Voxbone — sync to refresh, or add/edit entries manually</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {syncMsg && <span style={{ fontSize: 12, color: 'var(--ok)' }}>{syncMsg}</span>}
          <button className="btn btn-secondary btn-sm" onClick={() => { setAddMode(a => !a); setEditId(null); }}>
            <Plus size={13} /> {addMode ? 'Cancel Add' : 'Add New'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSync} disabled={syncing}>
            <RefreshCw size={13} style={syncing ? { animation: 'spin 0.8s linear infinite' } : undefined} />
            {syncing ? 'Syncing…' : 'Sync from Voxbone'}
          </button>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>URI</th>
              <th>Protocol</th>
              <th>SBC</th>
              <th style={{ width: 80 }}>Source</th>
              <th style={{ width: 130 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Add-new row at the top when active */}
            {addMode && (
              <tr style={{ background: 'var(--bg)' }}>
                <td>
                  <input
                    style={inputStyle}
                    value={addDraft.description}
                    onChange={e => setAddDraft(d => ({ ...d, description: e.target.value }))}
                    placeholder="Description"
                    autoFocus
                  />
                </td>
                <td>
                  <input
                    style={{ ...inputStyle, fontFamily: 'monospace' }}
                    value={addDraft.uri}
                    onChange={e => setAddDraft(d => ({ ...d, uri: e.target.value }))}
                    placeholder="sip:user@domain.com"
                  />
                </td>
                <td>
                  <select
                    className="select-input"
                    style={{ fontSize: 12, width: '100%' }}
                    value={addDraft.protocol}
                    onChange={e => setAddDraft(d => ({ ...d, protocol: e.target.value }))}
                  >
                    {PROTOCOLS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td>
                  <SbcPicker sbcs={sbcs} selected={addDraft.sbcIds} onChange={ids => setAddDraft(d => ({ ...d, sbcIds: ids }))} />
                </td>
                <td><span className="badge badge-gray">Local</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="btn btn-primary btn-xs"
                      onClick={handleAdd}
                      disabled={saving || !addDraft.uri.trim()}
                    >
                      <Check size={10} /> Save
                    </button>
                    <button className="btn btn-secondary btn-xs" onClick={() => setAddMode(false)}>
                      <X size={10} />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '24px 0' }}>Loading…</td></tr>
            ) : uris.length === 0 && !addMode ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '32px 0' }}>
                No Voice URIs yet. Use <strong style={{ color: 'var(--info)' }}>Sync from Voxbone</strong> or <strong style={{ color: 'var(--info)' }}>Add New</strong>.
              </td></tr>
            ) : uris.map(u => (
              editId === u.id ? (
                <tr key={u.id} style={{ background: 'var(--bg)' }}>
                  <td>
                    <input
                      style={inputStyle}
                      value={editDraft.description}
                      onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))}
                      placeholder="Description"
                      autoFocus
                    />
                  </td>
                  <td>
                    <input
                      style={{ ...inputStyle, fontFamily: 'monospace' }}
                      value={editDraft.uri}
                      onChange={e => setEditDraft(d => ({ ...d, uri: e.target.value }))}
                      placeholder="sip:user@domain.com"
                    />
                  </td>
                  <td>
                    <select
                      className="select-input"
                      style={{ fontSize: 12, width: '100%' }}
                      value={editDraft.protocol}
                      onChange={e => setEditDraft(d => ({ ...d, protocol: e.target.value }))}
                    >
                      {PROTOCOLS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td>
                    <SbcPicker sbcs={sbcs} selected={editDraft.sbcIds} onChange={ids => setEditDraft(d => ({ ...d, sbcIds: ids }))} />
                  </td>
                  <td>
                    {u.voxbone_id
                      ? <span className="badge badge-blue">Voxbone</span>
                      : <span className="badge badge-gray">Local</span>
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-primary btn-xs" onClick={() => handleSave(u.id)} disabled={saving}>
                        <Check size={10} /> Save
                      </button>
                      <button className="btn btn-secondary btn-xs" onClick={() => setEditId(null)}>
                        <X size={10} />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={u.id}>
                  <td style={{ fontWeight: 500 }}>{u.description || <span style={{ color: 'var(--muted-3)' }}>—</span>}</td>
                  <td><span className="tag font-mono" style={{ fontSize: 11 }}>{u.uri || '—'}</span></td>
                  <td><span style={{ fontSize: 11, color: 'var(--muted)' }}>{u.protocol}</span></td>
                  <td>
                    {u.sbc_name
                      ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>{u.sbc_name}</span>
                      : <span style={{ color: 'var(--muted-3)' }}>—</span>
                    }
                  </td>
                  <td>
                    {u.voxbone_id
                      ? <span className="badge badge-blue">Voxbone</span>
                      : <span className="badge badge-gray">Local</span>
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-secondary btn-xs" onClick={() => startEdit(u)}>
                        <Edit2 size={10} /> Edit
                      </button>
                      <button
                        className="btn btn-secondary btn-xs"
                        style={{ color: 'var(--err)' }}
                        onClick={() => handleDelete(u.id, u.description)}
                      >
                        <Trash2 size={10} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── SBC management tab ───────────────────────────────────────────────────────

const EMPTY_SBC = {
  name: '', model: '', location: '', ip: '', public_ip: '',
  status: 'active', firmware: '', max_calls: 0, active_calls: 0,
  cpu_pct: 0, mem_pct: 0, sip_trunk_status: 'up', uptime: '',
  sip_port: '5067', media_ports: '6000-7999', tls_version: 'TLSv1.2 / TLSv1.3',
  srtp: 'Enabled (AES-128-CM)', codec_order: 'G.711u, G.711a, G.729, G.722',
  dtmf_mode: 'RFC 2833', sip_signaling: 'TCP + TLS', notes: '',
  api_url: '', api_user: '', api_password: '', api_insecure: 1, fqdn: '',
};

function fmtPolled(ts) {
  if (!ts) return 'Never polled';
  try {
    const d = new Date(ts);
    const diff = Math.round((Date.now() - d) / 1000);
    if (diff < 60)   return `Polled ${diff}s ago`;
    if (diff < 3600) return `Polled ${Math.round(diff/60)}m ago`;
    return `Polled ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } catch { return 'Polled'; }
}

function fmtUptime(raw) {
  if (!raw) return '—';
  const secs = Number(raw);
  if (isNaN(secs) || secs <= 0) return String(raw); // already formatted string
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || parts.length === 0) parts.push(`${m}m`);
  return parts.join(' ');
}

// ── Carriers tab (PSTN/ITSP trunk registry) ──────────────────────────────────
const EMPTY_CARRIER = { name: '', kind: 'itsp', routing_mode: 'sbc-only', peer: '', country_scope: '', notes: '', color: '#64748b' };
const ROUTING_MODES = [
  ['voxbone-api',   'Voxbone API (Voice URI + SBC)'],
  ['bandwidth-api', 'Bandwidth API (Dashboard)'],
  ['sbc-only',      'SBC only (dial-plan rule)'],
];
const MODE_LABEL = { 'voxbone-api': 'Voxbone API', 'bandwidth-api': 'Bandwidth API', 'sbc-only': 'SBC only' };

function CarriersTab({ onFlash }) {
  const [carriers, setCarriers] = useState([]);
  const [sbcs,     setSbcs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editId,   setEditId]   = useState(null); // carrier id | 'new' | null
  const [draft,    setDraft]    = useState(EMPTY_CARRIER);
  const [mapDraft, setMapDraft] = useState({}); // sbcId → {ip_group, dial_plan_name}
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([listCarriers(), listSbcs()]);
      setCarriers(c.carriers || []);
      setSbcs(s.sbcs || []);
    } catch (e) { onFlash('error', e.message); }
    finally { setLoading(false); }
  }, [onFlash]);
  useEffect(() => { load(); }, [load]);

  function openEdit(c) {
    setEditId(c.id);
    setDraft({ name: c.name, kind: c.kind, routing_mode: c.routing_mode, peer: c.peer, country_scope: c.country_scope, notes: c.notes, color: c.color || '#64748b' });
    const m = {};
    for (const s of (c.sbcs || [])) m[s.sbc_id] = { ip_group: s.ip_group, dial_plan_name: s.dial_plan_name };
    setMapDraft(m);
  }
  function openAdd() { setEditId('new'); setDraft(EMPTY_CARRIER); setMapDraft({}); }
  function cancel()  { setEditId(null); setDraft(EMPTY_CARRIER); setMapDraft({}); }

  async function save() {
    if (!draft.name.trim()) return onFlash('error', 'Carrier name is required');
    setSaving(true);
    try {
      let id = editId;
      if (editId === 'new') { const r = await createCarrier(draft); id = r.id; }
      else                  { await updateCarrier(editId, draft); }
      // Persist the per-SBC trunk mapping (only rows with an IP-Group set).
      const maps = Object.entries(mapDraft)
        .filter(([, v]) => (v.ip_group || '').trim() || (v.dial_plan_name || '').trim())
        .map(([sbc_id, v]) => ({ sbc_id: Number(sbc_id), ip_group: v.ip_group, dial_plan_name: v.dial_plan_name }));
      await setCarrierSbcs(id, maps);
      onFlash('success', editId === 'new' ? 'Carrier added' : 'Carrier updated');
      cancel();
      await load();
    } catch (e) { onFlash('error', e.message); }
    finally { setSaving(false); }
  }

  async function remove(c) {
    if (!window.confirm(`Delete carrier "${c.name}"?`)) return;
    try { await deleteCarrier(c.id); onFlash('success', 'Carrier deleted'); await load(); }
    catch (e) { onFlash('error', e.message); }
  }

  const [inferring, setInferring] = useState(false);
  async function runInfer() {
    setInferring(true);
    try {
      const r = await inferCarriers(false);
      onFlash('success', r.updated > 0
        ? `Tagged ${r.updated} number${r.updated === 1 ? '' : 's'} with their carrier (from synced dial plans)`
        : (r.note || 'No numbers to tag — open each SBC’s Dial Plan once to sync, then retry'));
    } catch (e) { onFlash('error', e.message); }
    finally { setInferring(false); }
  }

  const inp = { background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-2)', padding: '5px 9px', fontSize: 12, width: '100%' };
  // Neutral, informational pill — routing mode is a fact, not a warning.
  const modeBadge = (m) => (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
      background: 'var(--bg)', border: '1px solid var(--border-strong)', color: 'var(--muted-2)',
    }}>{MODE_LABEL[m] || m}</span>
  );
  const dot = (color) => (
    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: color || '#64748b', flexShrink: 0 }} />
  );

  function EditForm() {
    return (
      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Name
            <input style={inp} value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="Bandwidth International" />
          </label>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Kind
            <select style={inp} value={draft.kind} onChange={e => setDraft(d => ({ ...d, kind: e.target.value }))}>
              <option value="voxbone">voxbone</option>
              <option value="bandwidth">bandwidth</option>
              <option value="itsp">itsp</option>
            </select>
          </label>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Routing mode
            <select style={inp} value={draft.routing_mode} onChange={e => setDraft(d => ({ ...d, routing_mode: e.target.value }))}>
              {ROUTING_MODES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Country scope
            <input style={inp} value={draft.country_scope} onChange={e => setDraft(d => ({ ...d, country_scope: e.target.value }))} placeholder="International / US-CA / IE" />
          </label>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>SIP peer (info)
            <input style={inp} value={draft.peer} onChange={e => setDraft(d => ({ ...d, peer: e.target.value }))} placeholder="outbound.voxbone.com" />
          </label>
          <label style={{ fontSize: 11, color: 'var(--muted-2)' }}>Colour
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="color" value={draft.color} onChange={e => setDraft(d => ({ ...d, color: e.target.value }))}
                style={{ width: 38, height: 30, padding: 0, border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--bg)', cursor: 'pointer' }} />
              <input style={inp} value={draft.color} onChange={e => setDraft(d => ({ ...d, color: e.target.value }))} placeholder="#64748b" />
            </div>
          </label>
          <label style={{ fontSize: 11, color: 'var(--muted-2)', gridColumn: '1 / -1' }}>Notes
            <input style={inp} value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} />
          </label>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted-2)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Trunk mapping per SBC
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 8 }}>
          For each SBC this carrier is present on, give its IP-Group (trunk) name and the inbound dial-plan. Leave both blank to omit an SBC.
        </div>
        <table style={{ width: '100%', marginBottom: 12 }}>
          <thead><tr>
            <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--muted-3)', padding: '4px 0' }}>SBC</th>
            <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--muted-3)' }}>IP-Group</th>
            <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--muted-3)' }}>Dial plan</th>
          </tr></thead>
          <tbody>
            {sbcs.map(s => {
              const v = mapDraft[s.id] || { ip_group: '', dial_plan_name: '' };
              const set = (field, val) => setMapDraft(m => ({ ...m, [s.id]: { ...v, [field]: val } }));
              return (
                <tr key={s.id}>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', padding: '4px 8px 4px 0', whiteSpace: 'nowrap' }}>{s.name}</td>
                  <td style={{ padding: '4px 6px 4px 0' }}><input style={inp} value={v.ip_group} onChange={e => set('ip_group', e.target.value)} placeholder="IP-Group name" /></td>
                  <td style={{ padding: '4px 0' }}><input style={inp} value={v.dial_plan_name} onChange={e => set('dial_plan_name', e.target.value)} placeholder="Dial plan name" /></td>
                </tr>
              );
            })}
            {sbcs.length === 0 && <tr><td colSpan={3} style={{ fontSize: 12, color: 'var(--muted-3)', padding: 8 }}>No SBCs configured.</td></tr>}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}><Check size={13} /> {saving ? 'Saving…' : 'Save Carrier'}</button>
          <button className="btn btn-secondary btn-sm" onClick={cancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Server size={15} color="var(--info)" />
          <div className="card-title">Carriers</div>
          <span style={{ fontSize: 11, color: 'var(--muted-3)' }}>PSTN / ITSP trunks a number can originate on</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={runInfer} disabled={inferring} title="Tag numbers with their carrier from the synced SBC dial plans (does not touch the SBC; only fills blanks)">
            {inferring ? 'Inferring…' : 'Infer carriers'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={openAdd} disabled={editId !== null}><Plus size={13} /> Add Carrier</button>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}><RefreshCw size={13} /></button>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {editId === 'new' && <EditForm />}
        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '30px 0' }}>Loading…</div>
        ) : carriers.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '30px 0' }}>No carriers yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Carrier</th><th>Scope</th><th>Routing</th><th>Trunk(s)</th><th>Peer</th><th></th>
              </tr></thead>
              <tbody>
                {carriers.map(c => (
                  <Fragment key={c.id}>
                    <tr>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          {dot(c.color)}<span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{c.name}</span>
                        </span>
                        {c.notes && <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 2 }}>{c.notes}</div>}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--muted-2)' }}>{c.country_scope || '—'}</td>
                      <td>{modeBadge(c.routing_mode)}</td>
                      <td style={{ fontSize: 11, color: 'var(--muted-2)' }}>
                        {(c.sbcs || []).length === 0 ? <span style={{ color: 'var(--muted-3)' }}>—</span> :
                          (c.sbcs || []).map(s => (
                            <div key={s.sbc_id}>{s.sbc_name}: <span className="font-mono" style={{ color: 'var(--purple)' }}>{s.ip_group}</span> → {s.dial_plan_name}</div>
                          ))}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--muted-3)', fontFamily: 'monospace' }}>{c.peer || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-secondary btn-xs" onClick={() => openEdit(c)} disabled={editId !== null}><Edit2 size={11} /></button>{' '}
                        <button className="btn btn-secondary btn-xs" onClick={() => remove(c)} disabled={editId !== null}><Trash2 size={11} /></button>
                      </td>
                    </tr>
                    {editId === c.id && <tr><td colSpan={6}><EditForm /></td></tr>}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SbcTab({ onFlash }) {
  const [sbcs,       setSbcs]      = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [editSbc,    setEditSbc]   = useState(null); // null | sbc-object | 'new'
  const [draft,      setDraft]     = useState(EMPTY_SBC);
  const [saving,     setSaving]    = useState(false);
  const [pollingIds,    setPollingIds]   = useState(new Set());
  const [dialPlanVisible, setDialPlanVisible] = useState(new Set());
  const [dialPlanData,    setDialPlanData]    = useState({});
  const [dpDraft,         setDpDraft]         = useState({}); // sbcId → {planName,name,prefix,tag}
  const [dpSaving,        setDpSaving]        = useState(new Set());
  const [rawCsvVisible,   setRawCsvVisible]   = useState(new Set());
  const [rawCsvData,      setRawCsvData]      = useState({}); // sbcId → {csv, loading, error}
  const [fullCfgVisible,  setFullCfgVisible]  = useState(new Set());
  const [fullCfgData,     setFullCfgData]     = useState({}); // sbcId → {config, loading, error}
  const [pollInterval,  setPollInterval] = useState(
    () => Number(localStorage.getItem('sbc_poll_interval') || 300)
  );
  const [psStatus,    setPsStatus]    = useState({ loading: true }); // { loading } | { pwsh, module }
  const [psInstalling, setPsInstalling] = useState(false);

  async function loadDialPlan(sbc) {
    try {
      const r = await getSbcDialPlan(sbc.id);
      setDialPlanData(p => ({ ...p, [sbc.id]: r.entries || [] }));
    } catch {
      setDialPlanData(p => ({ ...p, [sbc.id]: [] }));
    }
  }

  async function handleAddDpEntry(sbc) {
    const d = dpDraft[sbc.id] || {};
    if (!d.prefix?.trim()) return onFlash('error', 'Prefix (E.164) is required');
    setDpSaving(p => new Set([...p, sbc.id]));
    try {
      const entries = dialPlanData[sbc.id] || [];
      const planName = d.planName?.trim() || 'Dial Plan';
      const matchedGroup = entries.find(e => e.planName === planName);
      const r = await addSbcDialPlanEntry(sbc.id, {
        planName,
        name:   d.name?.trim()  || d.prefix.trim(),
        prefix: d.prefix.trim(),
        tag:    d.tag?.trim()   || '',
        dpIdx:  matchedGroup?.dpIdx ?? 0,
      });
      setDialPlanData(p => ({ ...p, [sbc.id]: r.entries || [] }));
      setDpDraft(p => ({ ...p, [sbc.id]: { planName: d.planName || '', name: '', prefix: '', tag: d.tag || '' } }));
      onFlash('success', `Added ${d.prefix} to dial plan`);
    } catch (err) {
      onFlash('error', err.message);
    } finally {
      setDpSaving(p => { const n = new Set(p); n.delete(sbc.id); return n; });
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sbcRes, cfgRes, psRes] = await Promise.allSettled([listSbcs(), getSbcPollConfig(), getPowershellStatus()]);
      if (sbcRes.status === 'fulfilled') setSbcs(sbcRes.value.sbcs || []);
      else onFlash('error', sbcRes.reason.message);
      if (cfgRes.status === 'fulfilled') {
        const secs = cfgRes.value.intervalSecs;
        setPollInterval(secs);
        localStorage.setItem('sbc_poll_interval', secs);
      }
      setPsStatus(psRes.status === 'fulfilled' ? psRes.value : { pwsh: false, module: false });
    } finally {
      setLoading(false);
    }
  }, [onFlash]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh SBC metrics at the configured interval
  useEffect(() => {
    if (pollInterval < 10) return; // guard against 0/negative
    const tick = async () => {
      const current = await listSbcs().catch(() => null);
      if (current) setSbcs(current.sbcs || []);
    };
    const id = setInterval(tick, pollInterval * 1000);
    return () => clearInterval(id);
  }, [pollInterval]);

  // Clear password when opening edit (server never returns it)
  function openEdit(sbc) { setDraft({ ...EMPTY_SBC, ...sbc, api_password: '' }); setEditSbc(sbc); }
  function openAdd()     { setDraft({ ...EMPTY_SBC }); setEditSbc('new'); }
  function closeModal()  { setEditSbc(null); }
  function set(field, value) { setDraft(d => ({ ...d, [field]: value })); }

  async function handleSave() {
    if (!draft.name?.trim()) return onFlash('error', 'Name is required');
    setSaving(true);
    try {
      if (editSbc === 'new') {
        await createSbc(draft);
        onFlash('success', `SBC "${draft.name}" added`);
      } else {
        await updateSbc(editSbc.id, draft);
        onFlash('success', `SBC "${draft.name}" updated`);
      }
      closeModal();
      await load();
    } catch (err) {
      onFlash('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(sbc) {
    if (!window.confirm(`Delete "${sbc.name}"? This cannot be undone.`)) return;
    try {
      await deleteSbc(sbc.id);
      setSbcs(prev => prev.filter(s => s.id !== sbc.id));
      onFlash('success', `SBC "${sbc.name}" deleted`);
    } catch (err) {
      onFlash('error', err.message);
    }
  }

  async function handlePoll(sbc) {
    if (!sbc.api_url) return onFlash('error', `Configure an API URL for ${sbc.name} first`);
    setPollingIds(p => new Set([...p, sbc.id]));
    try {
      const r = await pollSbc(sbc.id);
      setSbcs(prev => prev.map(s => s.id === sbc.id ? { ...s, ...(r.sbc || {}) } : s));
      const msg = r.errors?.length
        ? `${sbc.name}: ${r.fetched} metric(s) updated — ${r.errors.join(' | ')}`
        : `${sbc.name}: ${r.fetched} metric(s) refreshed from SBC`;
      onFlash(r.errors?.length ? 'warn' : 'success', msg);
    } catch (err) {
      onFlash('error', `Poll failed: ${err.message}`);
    } finally {
      setPollingIds(p => { const n = new Set(p); n.delete(sbc.id); return n; });
    }
  }

  const statusColor = s => ({ active: 'var(--ok)', maintenance: 'var(--warn)', offline: 'var(--err)' }[s] || 'var(--muted)');
  const trunkColor  = s => ({ up: 'var(--ok)', down: 'var(--err)', degraded: 'var(--warn)' }[s] || 'var(--muted)');

  // Shared styles used inside the modal
  const inp     = { background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-2)', padding: '6px 10px', fontSize: 12, width: '100%', boxSizing: 'border-box' };
  const fldLbl  = { fontSize: 11, color: 'var(--muted-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' };
  const secHdr  = { fontSize: 11, color: 'var(--muted-3)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10, marginTop: 20, borderBottom: '1px solid var(--border)', paddingBottom: 6 };

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Session Border Controllers</div>
            <div className="card-sub">Configure SBC instances — admin only</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)' }}>
              Auto-refresh every
              <input
                type="number" min="10" max="3600"
                value={pollInterval}
                onChange={e => {
                  const v = Math.max(10, Number(e.target.value) || 300);
                  setPollInterval(v);
                  localStorage.setItem('sbc_poll_interval', v);
                  setSbcPollConfig(v).catch(() => {});
                }}
                style={{ width: 60, padding: '2px 6px', background: 'var(--border)', border: '1px solid var(--muted-3)', borderRadius: 6, color: 'var(--text)', fontSize: 12, textAlign: 'center' }}
              />
              s
            </label>
            <button className="btn btn-primary btn-sm" onClick={openAdd}>
              <Plus size={13} /> Add SBC
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted-3)' }}>Loading…</div>
      ) : sbcs.length === 0 ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted-3)' }}>
            No SBCs configured. Click <strong style={{ color: 'var(--info)' }}>Add SBC</strong> to add one.
          </div>
        </div>
      ) : sbcs.map(sbc => {
        const callPct = sbc.max_calls ? Math.round((sbc.active_calls / sbc.max_calls) * 100) : 0;
        return (
          <div key={sbc.id} className="card" style={{ marginTop: 16 }}>
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--purple-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Server size={20} color="var(--purple)" />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{sbc.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>{sbc.model}{sbc.location ? ` · ${sbc.location}` : ''}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: statusColor(sbc.status), background: statusColor(sbc.status) + '22', padding: '3px 8px', borderRadius: 10 }}>
                  {(sbc.status || 'active').toUpperCase()}
                </span>
                {sbc.firmware && <span className="badge badge-gray">FW {sbc.firmware}</span>}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePoll(sbc)}
                  disabled={pollingIds.has(sbc.id)}
                  title={sbc.api_url ? 'Fetch live metrics from AudioCodes REST API' : 'Configure API URL first'}
                  style={{ color: sbc.api_url ? 'var(--info)' : 'var(--muted-3)' }}
                >
                  <RefreshCw size={12} style={pollingIds.has(sbc.id) ? { animation: 'spin 0.8s linear infinite' } : undefined} />
                  {pollingIds.has(sbc.id) ? 'Polling…' : 'Poll Live'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => openEdit(sbc)}>
                  <Edit2 size={12} /> Configure
                </button>
                <button className="btn btn-secondary btn-sm" style={{ color: 'var(--err)' }} onClick={() => handleDelete(sbc)}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>

            <div className="card-body">
              <div className="sbc-metrics-grid">
                <div className="sbc-metric">
                  <div className="sbc-metric-label">Active Calls</div>
                  <div className="sbc-metric-value">{sbc.active_calls}</div>
                  <div className="sbc-metric-sub">of {sbc.max_calls} max ({callPct}%)</div>
                </div>
                <div className="sbc-metric">
                  <div className="sbc-metric-label">CPU Load</div>
                  <div className="sbc-metric-value" style={{ color: sbc.cpu_pct > 70 ? 'var(--err)' : 'var(--text)' }}>{sbc.cpu_pct}%</div>
                  <div className="progress-bar" style={{ marginTop: 4 }}>
                    <div className="progress-fill" style={{ width: `${Math.min(sbc.cpu_pct, 100)}%`, background: sbc.cpu_pct > 70 ? 'var(--err)' : 'var(--purple)' }} />
                  </div>
                </div>
                <div className="sbc-metric">
                  <div className="sbc-metric-label">Memory</div>
                  <div className="sbc-metric-value">{sbc.mem_pct}%</div>
                  <div className="progress-bar" style={{ marginTop: 4 }}>
                    <div className="progress-fill" style={{ width: `${Math.min(sbc.mem_pct, 100)}%`, background: sbc.mem_pct > 80 ? 'var(--err)' : 'var(--purple)' }} />
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginTop: 4 }}>
                {[
                  ['Public IP',  sbc.public_ip || '—'],
                  ['Private IP', sbc.ip || '—'],
                  ['SIP Trunk',  sbc.sip_trunk_status || 'up'],
                  ['Uptime',     fmtUptime(sbc.uptime)],
                ].map(([lbl, val]) => (
                  <div key={lbl} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4 }}>{lbl}</div>
                    <div style={{ fontSize: 12, fontFamily: 'monospace', color: lbl === 'SIP Trunk' ? trunkColor(val) : 'var(--text-2)' }}>{val}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px 16px' }}>
                {[
                  ['SIP Port',    sbc.sip_port],
                  ['Media Ports', sbc.media_ports],
                  ['TLS',         sbc.tls_version],
                  ['SRTP',        sbc.srtp],
                  ['Codecs',      sbc.codec_order],
                  ['DTMF',        sbc.dtmf_mode],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                    <span style={{ color: 'var(--muted-3)', width: 80, flexShrink: 0 }}>{k}</span>
                    <span style={{ color: 'var(--muted)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                <span style={{ fontSize: 11, color: sbc.api_url ? 'var(--ok)' : 'var(--muted-3)' }}>
                  {sbc.api_url ? `API: ${sbc.api_url}` : 'API: not configured'}
                </span>
                <span style={{ fontSize: 11, color: 'var(--muted-3)', marginLeft: 'auto' }}>
                  {fmtPolled(sbc.last_polled)}
                </span>
              </div>
              {sbc.notes && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted-2)', fontStyle: 'italic' }}>
                  {sbc.notes}
                </div>
              )}

              <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ fontSize: 11 }}
                  onClick={() => {
                    if (dialPlanVisible.has(sbc.id)) {
                      setDialPlanVisible(p => { const n = new Set(p); n.delete(sbc.id); return n; });
                    } else {
                      setDialPlanVisible(p => new Set([...p, sbc.id]));
                      loadDialPlan(sbc);
                    }
                  }}
                >
                  {dialPlanVisible.has(sbc.id) ? '▲ Hide Dial Plan' : '▼ Dial Plan'}
                  {dialPlanData[sbc.id]?.length > 0 && (
                    <span style={{ marginLeft: 6, background: 'var(--purple)', color: 'var(--on-brand)', borderRadius: 10, fontSize: 10, padding: '1px 6px' }}>
                      {dialPlanData[sbc.id].length}
                    </span>
                  )}
                </button>

                <button
                  className="btn btn-secondary btn-sm"
                  style={{ fontSize: 11 }}
                  onClick={async () => {
                    const id = sbc.id;
                    if (rawCsvVisible.has(id)) {
                      setRawCsvVisible(p => { const n = new Set(p); n.delete(id); return n; });
                      return;
                    }
                    setRawCsvVisible(p => new Set([...p, id]));
                    setRawCsvData(p => ({ ...p, [id]: { loading: true } }));
                    try {
                      const r = await getSbcDialPlanRaw(id);
                      setRawCsvData(p => ({ ...p, [id]: { csv: r.csv || '' } }));
                    } catch (e) {
                      setRawCsvData(p => ({ ...p, [id]: { error: e.message } }));
                    }
                  }}
                >
                  {rawCsvVisible.has(sbc.id) ? '▲ Hide Raw CSV' : '▼ Raw CSV'}
                </button>

                <button
                  className="btn btn-secondary btn-sm"
                  style={{ fontSize: 11 }}
                  title="Full SBC running-config (carriers / IP-Groups / Proxy-Sets) — passwords redacted"
                  onClick={async () => {
                    const id = sbc.id;
                    if (fullCfgVisible.has(id)) {
                      setFullCfgVisible(p => { const n = new Set(p); n.delete(id); return n; });
                      return;
                    }
                    setFullCfgVisible(p => new Set([...p, id]));
                    setFullCfgData(p => ({ ...p, [id]: { loading: true } }));
                    try {
                      const r = await getSbcConfigFull(id);
                      setFullCfgData(p => ({ ...p, [id]: { config: r.config || '' } }));
                    } catch (e) {
                      setFullCfgData(p => ({ ...p, [id]: { error: e.message } }));
                    }
                  }}
                >
                  {fullCfgVisible.has(sbc.id) ? '▲ Hide Full Config' : '▼ Full Config'}
                </button>
                </div>

                {fullCfgVisible.has(sbc.id) && (
                  <div style={{ marginTop: 10 }}>
                    {fullCfgData[sbc.id]?.loading && (
                      <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>Fetching full config from SBC…</div>
                    )}
                    {fullCfgData[sbc.id]?.error && (
                      <div style={{ fontSize: 12, color: 'var(--err)' }}>{fullCfgData[sbc.id].error}</div>
                    )}
                    {fullCfgData[sbc.id]?.config !== undefined && !fullCfgData[sbc.id].loading && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 6 }}>
                          <button
                            className="btn btn-secondary btn-xs"
                            onClick={() => {
                              navigator.clipboard?.writeText(fullCfgData[sbc.id].config)
                                .then(() => onFlash('success', 'Config copied to clipboard'))
                                .catch(() => onFlash('error', 'Clipboard copy blocked — use Download'));
                            }}
                          >
                            Copy
                          </button>
                          <button
                            className="btn btn-secondary btn-xs"
                            onClick={() => {
                              const blob = new Blob([fullCfgData[sbc.id].config], { type: 'text/plain' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = `sbc-config-${sbc.name}.ini`; a.click();
                              URL.revokeObjectURL(url);
                            }}
                          >
                            Download
                          </button>
                        </div>
                        <pre style={{
                          background: '#0a0d14', border: '1px solid var(--border)', borderRadius: 6,
                          padding: 12, fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)',
                          overflowX: 'auto', whiteSpace: 'pre', margin: 0, maxHeight: 500, overflowY: 'auto',
                        }}>
                          {fullCfgData[sbc.id].config || '(empty config)'}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {rawCsvVisible.has(sbc.id) && (
                  <div style={{ marginTop: 10 }}>
                    {rawCsvData[sbc.id]?.loading && (
                      <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>Fetching from SBC…</div>
                    )}
                    {rawCsvData[sbc.id]?.error && (
                      <div style={{ fontSize: 12, color: 'var(--err)' }}>{rawCsvData[sbc.id].error}</div>
                    )}
                    {rawCsvData[sbc.id]?.csv !== undefined && !rawCsvData[sbc.id].loading && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                          <button
                            className="btn btn-secondary btn-xs"
                            onClick={() => {
                              const blob = new Blob([rawCsvData[sbc.id].csv], { type: 'text/csv' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = `dialplan-${sbc.name}.csv`; a.click();
                              URL.revokeObjectURL(url);
                            }}
                          >
                            Download CSV
                          </button>
                        </div>
                        <pre style={{
                          background: '#0a0d14', border: '1px solid var(--border)', borderRadius: 6,
                          padding: 12, fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)',
                          overflowX: 'auto', whiteSpace: 'pre', margin: 0,
                        }}>
                          {rawCsvData[sbc.id].csv || '(empty file)'}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {dialPlanVisible.has(sbc.id) && (() => {
                  const entries = dialPlanData[sbc.id] || [];
                  // Group entries by dpIdx / planName
                  const groups = [];
                  for (const e of entries) {
                    const g = groups.find(g => g.dpIdx === e.dpIdx);
                    if (g) g.entries.push(e);
                    else groups.push({ dpIdx: e.dpIdx ?? 0, planName: e.planName || 'Dial Plan', entries: [e] });
                  }
                  const planNames = [...new Set(entries.map(e => e.planName).filter(Boolean))];
                  const draft = dpDraft[sbc.id] || {};
                  // Auto-fill planName from first existing plan if not set
                  const activePlan = draft.planName || planNames[0] || '';

                  return (
                  <div style={{ marginTop: 10 }}>
                    {/* Add entry form */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      {planNames.length > 1 ? (
                        <select
                          value={activePlan}
                          onChange={e => setDpDraft(p => ({ ...p, [sbc.id]: { ...(p[sbc.id] || {}), planName: e.target.value } }))}
                          style={{ width: 150, padding: '4px 8px', background: 'var(--border)', border: '1px solid var(--muted-3)', borderRadius: 6, color: 'var(--text)', fontSize: 12 }}
                        >
                          {planNames.map(n => <option key={n} value={n}>{n}</option>)}
                          <option value="">+ New plan…</option>
                        </select>
                      ) : (
                        <input
                          value={activePlan}
                          onChange={e => setDpDraft(p => ({ ...p, [sbc.id]: { ...(p[sbc.id] || {}), planName: e.target.value } }))}
                          placeholder="Plan name"
                          style={{ width: 140, padding: '4px 8px', background: 'var(--border)', border: '1px solid var(--muted-3)', borderRadius: 6, color: 'var(--text)', fontSize: 12 }}
                        />
                      )}
                      {[
                        { key: 'name',   placeholder: 'Rule name',       width: 140 },
                        { key: 'prefix', placeholder: '+441234567890',    width: 150 },
                        { key: 'tag',    placeholder: 'Tag (e.g. Teams)', width: 130 },
                      ].map(({ key, placeholder, width }) => (
                        <input
                          key={key}
                          value={draft[key] || ''}
                          onChange={e => setDpDraft(p => ({ ...p, [sbc.id]: { ...(p[sbc.id] || {}), [key]: e.target.value } }))}
                          placeholder={placeholder}
                          style={{ width, padding: '4px 8px', background: 'var(--border)', border: '1px solid var(--muted-3)', borderRadius: 6, color: 'var(--text)', fontSize: 12 }}
                        />
                      ))}
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={dpSaving.has(sbc.id)}
                        onClick={() => handleAddDpEntry(sbc)}
                      >
                        <Plus size={12} /> {dpSaving.has(sbc.id) ? 'Adding…' : 'Add Entry'}
                      </button>
                    </div>

                    {!dialPlanData[sbc.id] ? (
                      <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>Loading…</div>
                    ) : entries.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>No dial plan entries on this SBC.</div>
                    ) : groups.map(group => (
                      <div key={group.dpIdx} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: 'var(--purple)', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {group.planName} <span style={{ color: 'var(--muted-3)', fontWeight: 400 }}>(dial-plan {group.dpIdx})</span>
                        </div>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            {['Rule Name', 'Prefix (E.164)', 'Tag'].map(h => (
                              <th key={h} style={{ textAlign: 'left', color: 'var(--muted-3)', fontWeight: 500, padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>{h}</th>
                            ))}
                            <th style={{ width: 60, borderBottom: '1px solid var(--border)' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.entries.map(e => (
                            <tr key={e.idx}>
                              <td style={{ padding: '4px 8px', color: 'var(--muted)' }}>{e.name || '—'}</td>
                              <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: 'var(--text-2)' }}>{e.prefix}</td>
                              <td style={{ padding: '4px 8px', color: 'var(--info)' }}>{e.tag || '—'}</td>
                              <td style={{ padding: '4px 8px' }}>
                                <button
                                  className="btn btn-secondary btn-xs"
                                  style={{ color: 'var(--err)', fontSize: 10 }}
                                  onClick={async () => {
                                    if (!window.confirm(`Remove ${e.prefix} from dial plan?`)) return;
                                    try {
                                      const r = await deleteSbcDialPlanEntry(sbc.id, e.idx);
                                      setDialPlanData(p => ({ ...p, [sbc.id]: r.entries || [] }));
                                      onFlash('success', `Removed ${e.prefix} from dial plan`);
                                    } catch (err) {
                                      onFlash('error', err.message);
                                    }
                                  }}
                                >
                                  <Trash2 size={10} /> Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })}

      {/* PowerShell status card */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <div>
            <div className="card-title">PowerShell / Teams Automation</div>
            <div className="card-sub">Server-side PowerShell enables automatic VRP assignment when a DID number is set on a Teams user</div>
          </div>
        </div>
        <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
          {psStatus.loading ? (
            <span style={{ fontSize: 13, color: 'var(--muted-3)' }}>Checking PowerShell status…</span>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                  background: psStatus.pwsh ? 'var(--ok)' : 'var(--err)',
                }} />
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  PowerShell 7 (pwsh): <strong style={{ color: psStatus.pwsh ? 'var(--ok)' : 'var(--err)' }}>
                    {psStatus.pwsh ? `Installed${psStatus.pwshVersion ? ` (${psStatus.pwshVersion})` : ''}` : 'Not found'}
                  </strong>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                  background: psStatus.module ? 'var(--ok)' : 'var(--warn)',
                }} />
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  MicrosoftTeams module: <strong style={{ color: psStatus.module ? 'var(--ok)' : 'var(--warn)' }}>
                    {psStatus.module ? 'Installed' : 'Not installed'}
                  </strong>
                </span>
              </div>
              {psStatus.pwsh && !psStatus.module && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ color: 'var(--info)' }}
                  disabled={psInstalling}
                  onClick={async () => {
                    setPsInstalling(true);
                    try {
                      await installPowershellModule();
                      const s = await getPowershellStatus();
                      setPsStatus(s);
                      onFlash('success', 'MicrosoftTeams module installed — VRP assignments will now apply automatically');
                    } catch (e) {
                      onFlash('error', `Install failed: ${e.message}`);
                    } finally {
                      setPsInstalling(false);
                    }
                  }}
                >
                  <RefreshCw size={12} style={psInstalling ? { animation: 'spin 0.8s linear infinite' } : undefined} />
                  {psInstalling ? 'Installing…' : 'Install MicrosoftTeams Module'}
                </button>
              )}
              {psStatus.pwsh && psStatus.module && (
                <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>
                  VRP assignments will be applied automatically via PowerShell when numbers are set on Teams users.
                </span>
              )}
              {!psStatus.pwsh && (
                <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>
                  Install PowerShell 7 on the server to enable automatic VRP assignment.
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit / Add modal */}
      {editSbc !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 24 }}>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                {editSbc === 'new' ? 'Add SBC' : `Configure: ${editSbc.name}`}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={closeModal}><X size={13} /> Close</button>
            </div>

            <div style={secHdr}>Identity</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={fldLbl}>Name *</div>
                <input style={inp} value={draft.name} onChange={e => set('name', e.target.value)} placeholder="SBC-WEST-01" autoFocus />
              </div>
              <div>
                <div style={fldLbl}>Model</div>
                <input style={inp} value={draft.model} onChange={e => set('model', e.target.value)} placeholder="AudioCodes Mediant 9000" />
              </div>
              <div>
                <div style={fldLbl}>Location</div>
                <input style={inp} value={draft.location} onChange={e => set('location', e.target.value)} placeholder="AWS us-west-2" />
              </div>
              <div>
                <div style={fldLbl}>Status</div>
                <select style={{ ...inp, cursor: 'pointer' }} value={draft.status} onChange={e => set('status', e.target.value)}>
                  {['active', 'maintenance', 'offline'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={secHdr}>Network</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={fldLbl}>Private IP</div>
                <input style={{ ...inp, fontFamily: 'monospace' }} value={draft.ip} onChange={e => set('ip', e.target.value)} placeholder="10.0.1.10" />
              </div>
              <div>
                <div style={fldLbl}>Public IP</div>
                <input style={{ ...inp, fontFamily: 'monospace' }} value={draft.public_ip} onChange={e => set('public_ip', e.target.value)} placeholder="54.201.12.45" />
              </div>
            </div>

            <div style={secHdr}>System</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={fldLbl}>Firmware</div>
                <input style={inp} value={draft.firmware} onChange={e => set('firmware', e.target.value)} placeholder="7.40A.258" />
              </div>
              <div>
                <div style={fldLbl}>Max Sessions</div>
                <input style={inp} type="number" min="0" value={draft.max_calls} onChange={e => set('max_calls', e.target.value)} />
              </div>
              <div>
                <div style={fldLbl}>Uptime</div>
                <input style={inp} value={draft.uptime} onChange={e => set('uptime', e.target.value)} placeholder="142d 6h 14m" />
              </div>
            </div>

            <div style={secHdr}>Live Metrics</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={fldLbl}>Active Calls</div>
                <input style={inp} type="number" min="0" value={draft.active_calls} onChange={e => set('active_calls', e.target.value)} />
              </div>
              <div>
                <div style={fldLbl}>CPU %</div>
                <input style={inp} type="number" min="0" max="100" value={draft.cpu_pct} onChange={e => set('cpu_pct', e.target.value)} />
              </div>
              <div>
                <div style={fldLbl}>Memory %</div>
                <input style={inp} type="number" min="0" max="100" value={draft.mem_pct} onChange={e => set('mem_pct', e.target.value)} />
              </div>
              <div>
                <div style={fldLbl}>SIP Trunk</div>
                <select style={{ ...inp, cursor: 'pointer' }} value={draft.sip_trunk_status} onChange={e => set('sip_trunk_status', e.target.value)}>
                  {['up', 'down', 'degraded'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={secHdr}>SIP Configuration</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={fldLbl}>SIP Port (TLS)</div>
                <input style={inp} value={draft.sip_port} onChange={e => set('sip_port', e.target.value)} placeholder="5067" />
              </div>
              <div>
                <div style={fldLbl}>Media Ports</div>
                <input style={inp} value={draft.media_ports} onChange={e => set('media_ports', e.target.value)} placeholder="6000-7999" />
              </div>
              <div>
                <div style={fldLbl}>TLS Version</div>
                <input style={inp} value={draft.tls_version} onChange={e => set('tls_version', e.target.value)} placeholder="TLSv1.2 / TLSv1.3" />
              </div>
              <div>
                <div style={fldLbl}>SRTP</div>
                <input style={inp} value={draft.srtp} onChange={e => set('srtp', e.target.value)} placeholder="Enabled (AES-128-CM)" />
              </div>
              <div>
                <div style={fldLbl}>Codec Order</div>
                <input style={inp} value={draft.codec_order} onChange={e => set('codec_order', e.target.value)} placeholder="G.711u, G.711a, G.729, G.722" />
              </div>
              <div>
                <div style={fldLbl}>DTMF Mode</div>
                <input style={inp} value={draft.dtmf_mode} onChange={e => set('dtmf_mode', e.target.value)} placeholder="RFC 2833" />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <div style={fldLbl}>SIP Signaling</div>
                <input style={inp} value={draft.sip_signaling} onChange={e => set('sip_signaling', e.target.value)} placeholder="TCP + TLS" />
              </div>
            </div>

            <div style={secHdr}>AudioCodes REST API</div>
            <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 12 }}>
              Used by "Poll Live" to fetch real-time metrics directly from the SBC.
              Leave blank to manage metrics manually.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <div style={fldLbl}>API Base URL</div>
                <input
                  style={{ ...inp, fontFamily: 'monospace' }}
                  value={draft.api_url || ''}
                  onChange={e => set('api_url', e.target.value)}
                  placeholder="https://10.0.1.10"
                />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <div style={fldLbl}>SBC Domain (FQDN)</div>
                <input
                  style={{ ...inp, fontFamily: 'monospace' }}
                  value={draft.fqdn || ''}
                  onChange={e => set('fqdn', e.target.value)}
                  placeholder="ireland.damovohosting.com"
                />
                <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 4 }}>
                  Used to build dial plan targets: <span style={{ color: 'var(--info)', fontFamily: 'monospace' }}>{`{account_code}.${draft.fqdn || 'fqdn'}`}</span>
                </div>
              </div>
              <div>
                <div style={fldLbl}>Username</div>
                <input style={inp} value={draft.api_user || ''} onChange={e => set('api_user', e.target.value)} placeholder="Admin" autoComplete="username" />
              </div>
              <div>
                <div style={fldLbl}>Password</div>
                <input
                  style={inp}
                  type="password"
                  value={draft.api_password || ''}
                  onChange={e => set('api_password', e.target.value)}
                  placeholder={editSbc !== 'new' ? 'Leave blank to keep existing' : 'Password'}
                  autoComplete="new-password"
                />
              </div>
              <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="sbc-api-insecure"
                  checked={!!draft.api_insecure}
                  onChange={e => set('api_insecure', e.target.checked ? 1 : 0)}
                />
                <label htmlFor="sbc-api-insecure" style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>
                  Allow self-signed TLS certificates (required for most AudioCodes devices)
                </label>
              </div>
            </div>

            <div style={secHdr}>Notes</div>
            <textarea
              style={{ ...inp, resize: 'vertical', minHeight: 64 }}
              value={draft.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Optional notes about this SBC"
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button className="btn btn-secondary btn-sm" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !draft.name?.trim()}>
                {saving ? 'Saving…' : (editSbc === 'new' ? 'Add SBC' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main admin page ───────────────────────────────────────────────────────────

export default function Admin() {
  const [tab, setTab] = useState('assign');

  // ── Shared ────────────────────────────────────────────────────────────────
  const [customers, setCustomers] = useState([]);
  const [msg, setMsg] = useState(null);
  const [sbcsList, setSbcsList] = useState([]);

  // Stable identity so the app-level 30s alerts poll (which re-renders this page)
  // doesn't change the onFlash prop passed to each tab — which would re-fire each
  // tab's data load and make the whole section flash. Values still refresh via
  // each tab's own silent interval.
  const flash = useCallback((type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  }, []);

  useEffect(() => {
    listSbcs().then(d => setSbcsList(d.sbcs || [])).catch(() => {});
  }, []);

  const loadCustomers = useCallback(() =>
    listCustomers().then(d => setCustomers(d.customers || [])).catch(console.error),
  []);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  // ── Assign tab ────────────────────────────────────────────────────────────
  const [search,         setSearch]         = useState('');
  const [results,        setResults]        = useState([]);
  const [searching,      setSearching]      = useState(false);
  const [selected,       setSelected]       = useState(new Set());
  const [targetCustomer, setTargetCustomer] = useState('');
  const [assigning,      setAssigning]      = useState(false);
  const [syncing,        setSyncing]        = useState(false);
  const [syncMsg,        setSyncMsg]        = useState(null);

  async function handleSync() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const r = await syncDids();
      setSyncMsg(`${r.count} DIDs synced`);
      flash('success', `Synced ${r.count} DIDs from Voxbone`);
    } catch (e) {
      flash('error', e.message);
    } finally {
      setSyncing(false);
    }
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    setSelected(new Set());
    try {
      const data = await listDids({ search: search.trim() });
      setResults(data.dids || []);
    } catch (e) {
      flash('error', e.message);
    } finally {
      setSearching(false);
    }
  }

  async function handleAssign() {
    if (!selected.size)        return flash('warn', 'Select at least one number');
    if (targetCustomer === '') return flash('warn', 'Choose a customer first');
    setAssigning(true);
    try {
      await assignDids([...selected], targetCustomer === 'unassign' ? null : Number(targetCustomer));
      flash('success', `Assigned ${selected.size} number(s)`);
      setSelected(new Set());
      const data = await listDids({ search: search.trim() });
      setResults(data.dids || []);
    } catch (e) {
      flash('error', e.message);
    } finally {
      setAssigning(false);
    }
  }

  function toggleRow(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const allSelected = results.length > 0 && selected.size === results.length;

  // ── Customers tab ─────────────────────────────────────────────────────────
  const [newC, setNewC]       = useState({ name: '', email: '', password: '', accountCode: '', isAdmin: false });
  const [creating, setCreating] = useState(false);

  // Inline account-code editing
  const [editingCode, setEditingCode] = useState(null); // { id, value }

  // Expanded email panels
  const [expandedId, setExpandedId] = useState(null);

  // Edit customer modal
  const [editCustomer, setEditCustomer] = useState(null); // null | customer object
  const [editDraftC,   setEditDraftC]   = useState({});
  const [savingC,      setSavingC]      = useState(false);
  const [customerVrps, setCustomerVrps] = useState([]);
  // Enabled modules for the customer being edited (array of ids). Starts as all.
  const [editModules, setEditModules] = useState(CUSTOMER_MODULE_IDS);
  // Per-customer Voxbone override. key is only ever populated by admin typing a
  // new one; clearKey flags removal of an existing key on save.
  const [editVox, setEditVox] = useState({ base: '', authType: '', hasKey: false, key: '', clearKey: false });
  const [voxOpen, setVoxOpen] = useState(false);
  // Voxbone connection-test state: { state: 'idle'|'testing'|'ok'|'fail', msg }
  const [voxTest, setVoxTest] = useState({ state: 'idle', msg: '' });

  async function runVoxTest() {
    if (!editCustomer) return;
    setVoxTest({ state: 'testing', msg: '' });
    try {
      const r = await testCustomerVoxbone(editCustomer.id);
      if (r.ok) {
        const where = r.override ? "customer's own trusted key → writes go live" : 'sandbox write-default → writes stay in sandbox';
        setVoxTest({ state: 'ok', msg: `Connected (${where}) — ${r.count} number${r.count === 1 ? '' : 's'} visible on ${r.base}` });
      } else {
        setVoxTest({ state: 'fail', msg: `${r.error || 'Failed'}${r.status ? ` (HTTP ${r.status})` : ''}` });
      }
    } catch (e) {
      setVoxTest({ state: 'fail', msg: e.message || 'Request failed' });
    }
  }

  function openEditCustomer(c) {
    setEditDraftC({ name: c.name, email: c.email, password: '', accountCode: c.account_code || '', isAdmin: !!c.is_admin, defaultVrpName: c.default_vrp_name || '', sbcId: c.sbc_id || '' });
    setEditCustomer(c);
    setCustomerVrps([]);
    setEditModules(CUSTOMER_MODULE_IDS);
    setEditVox({ base: '', authType: '', hasKey: false, key: '', clearKey: false });
    setVoxOpen(false);
    setVoxTest({ state: 'idle', msg: '' });
    listVoiceRoutingPolicies(c.id).then(d => setCustomerVrps(d.policies || [])).catch(() => {});
    // null from the API means "no restriction" → show all as enabled
    getCustomerModules(c.id).then(d => setEditModules(d.modules || CUSTOMER_MODULE_IDS)).catch(() => {});
    getCustomerVoxbone(c.id).then(d => setEditVox({ base: d.base || '', authType: d.authType || '', hasKey: !!d.hasKey, key: '', clearKey: false })).catch(() => {});
  }

  function toggleModule(id) {
    setEditModules(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  }

  async function handleSaveCustomer() {
    if (!editDraftC.name?.trim())  return flash('error', 'Name is required');
    if (!editDraftC.email?.trim()) return flash('error', 'Email is required');
    setSavingC(true);
    try {
      await updateCustomer(editCustomer.id, { ...editDraftC, sbcId: editDraftC.sbcId });
      // All modules enabled → null (no restriction); otherwise store the subset
      const mods = editModules.length === CUSTOMER_MODULE_IDS.length ? null : editModules;
      await setCustomerModules(editCustomer.id, mods);
      // Voxbone override: always persist base/authType; only touch the key when
      // the admin typed a new one, or explicitly cleared it.
      const voxPayload = { base: editVox.base, authType: editVox.authType };
      if (editVox.clearKey) voxPayload.key = '__clear__';
      else if (editVox.key && editVox.key.trim()) voxPayload.key = editVox.key.trim();
      await setCustomerVoxbone(editCustomer.id, voxPayload);
      flash('success', `Updated ${editDraftC.name}`);
      setEditCustomer(null);
      loadCustomers();
    } catch (e) {
      flash('error', e.message);
    } finally {
      setSavingC(false);
    }
  }

  async function handleCreateCustomer(e) {
    e.preventDefault();
    setCreating(true);
    try {
      await createCustomer(newC);
      flash('success', `Created ${newC.name}`);
      setNewC({ name: '', email: '', password: '', accountCode: '', isAdmin: false });
      loadCustomers();
    } catch (e) {
      flash('error', e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteCustomer(id, name) {
    if (!window.confirm(`Delete "${name}"? Their numbers will become unassigned.`)) return;
    try {
      await deleteCustomer(id);
      setCustomers(prev => prev.filter(c => c.id !== id));
      if (expandedId === id) setExpandedId(null);
      flash('success', `Deleted ${name}`);
    } catch (e) {
      flash('error', e.message);
    }
  }

  async function handleSaveCode(id, value) {
    setEditingCode(null);
    try {
      await updateCustomer(id, { accountCode: value });
      setCustomers(prev => prev.map(c => c.id === id ? { ...c, account_code: value } : c));
    } catch (e) {
      flash('error', e.message);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="content">
      {msg && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          padding: '10px 16px', borderRadius: 8, fontSize: 13,
          background: msg.type === 'success' ? 'var(--ok-bg)' : msg.type === 'warn' ? 'var(--warn-bg)' : 'var(--err-btn-bg)',
          color:      msg.type === 'success' ? 'var(--ok)' : msg.type === 'warn' ? 'var(--warn)' : 'var(--err)',
          border:     `1px solid ${msg.type === 'success' ? '#16653444' : msg.type === 'warn' ? 'var(--warn-border)' : 'var(--err-border)'}`,
        }}>{msg.text}</div>
      )}

      {/* Sync bar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div>
            <div className="card-title">Voxbone DID Cache</div>
            <div className="card-sub">Pull the full DID inventory from Voxbone into the local database</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {syncMsg && <span style={{ fontSize: 12, color: 'var(--ok)' }}>{syncMsg}</span>}
            <button className="btn btn-primary btn-sm" onClick={handleSync} disabled={syncing}>
              <RefreshCw size={13} style={syncing ? { animation: 'spin 0.8s linear infinite' } : undefined} />
              {syncing ? 'Syncing…' : 'Sync from Voxbone'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {[['assign', 'Assign Numbers'], ['customers', 'Manage Customers'], ['voiceuris', 'Voice URIs'], ['carriers', 'Carriers'], ['sbc', 'SBC'], ['changelog', 'Change Log'], ['system', 'System']].map(([id, label]) => (
          <button
            key={id}
            className={`btn btn-sm ${tab === id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab(id)}
          >{label}</button>
        ))}
      </div>

      {/* ── Assign Numbers tab ─────────────────────────────────────────── */}
      {tab === 'assign' && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Assign Numbers to Customer</div>
              <div className="card-sub">Search by partial number, select rows, choose customer, click Assign</div>
            </div>
          </div>

          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
              <div className="search-bar" style={{ flex: 1 }}>
                <Search size={14} color="var(--muted-3)" />
                <input
                  placeholder="Enter part of a number, e.g. +44192"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <button className="btn btn-secondary btn-sm" type="submit" disabled={searching}>
                <Search size={13} /> {searching ? 'Searching…' : 'Search'}
              </button>
            </form>
          </div>

          {results.length > 0 && (
            <>
              <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>
                  {results.length} numbers found · {selected.size} selected
                </span>
                <button className="btn btn-secondary btn-xs" onClick={() => setSelected(new Set(results.map(d => d.didId)))}>
                  Select All
                </button>
                {selected.size > 0 && (
                  <button className="btn btn-secondary btn-xs" onClick={() => setSelected(new Set())}>Clear</button>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    className="select-input"
                    value={targetCustomer}
                    onChange={e => setTargetCustomer(e.target.value)}
                    style={{ fontSize: 12 }}
                  >
                    <option value="">— Select customer —</option>
                    {customers.filter(c => !c.is_admin).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                    <option value="unassign">⊘ Remove assignment</option>
                  </select>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleAssign}
                    disabled={assigning || !selected.size || targetCustomer === ''}
                  >
                    <UserCheck size={13} />
                    {assigning ? 'Assigning…' : `Assign ${selected.size ? `(${selected.size})` : ''}`}
                  </button>
                </div>
              </div>

              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => allSelected
                            ? setSelected(new Set())
                            : setSelected(new Set(results.map(d => d.didId)))
                          }
                        />
                      </th>
                      <th>Number</th>
                      <th>Description</th>
                      <th>Country</th>
                      <th>Assigned To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(d => (
                      <tr
                        key={d.didId}
                        onClick={() => toggleRow(d.didId)}
                        style={{ cursor: 'pointer', background: selected.has(d.didId) ? 'var(--row-selected)' : undefined }}
                      >
                        <td onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(d.didId)} onChange={() => toggleRow(d.didId)} />
                        </td>
                        <td><span className="tag font-mono">{d.e164}</span></td>
                        <td style={{ color: 'var(--muted)', fontSize: 12 }}>{d.description || '—'}</td>
                        <td style={{ color: 'var(--muted-2)', fontSize: 12 }}>{d.countryCodeA3 || '—'}</td>
                        <td>
                          {d.customerName
                            ? <span style={{ fontSize: 12, color: 'var(--info)' }}>{d.customerName}</span>
                            : <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>Unassigned</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {results.length === 0 && search && !searching && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--muted-3)', fontSize: 13 }}>
              No numbers found matching "{search}"
            </div>
          )}
          {!search && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--muted-3)', fontSize: 13 }}>
              Enter a number prefix to search the DID inventory
            </div>
          )}
        </div>
      )}

      {/* ── Voice URIs tab ─────────────────────────────────────────────── */}
      {tab === 'voiceuris' && <VoiceUrisTab onFlash={flash} />}

      {/* ── SBC tab ────────────────────────────────────────────────────── */}
      {tab === 'carriers' && <CarriersTab onFlash={flash} />}

      {tab === 'sbc' && <SbcTab onFlash={flash} />}

      {tab === 'changelog' && <ChangeLogTab onFlash={flash} />}

      {tab === 'system' && <SystemTab onFlash={flash} />}

      {/* ── Manage Customers tab ───────────────────────────────────────── */}
      {tab === 'customers' && (
        <div style={{ display: 'grid', gap: 16 }}>

          {/* Add customer form */}
          <div className="card">
            <div className="card-header"><div className="card-title">Add Customer</div></div>
            <form onSubmit={handleCreateCustomer} style={{ padding: '16px 20px' }}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Customer Name</label>
                  <input className="form-input" value={newC.name} onChange={e => setNewC(p => ({...p, name: e.target.value}))} placeholder="Company or user name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Primary Email (login)</label>
                  <input className="form-input" type="email" value={newC.email} onChange={e => setNewC(p => ({...p, email: e.target.value}))} placeholder="user@example.com" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input className="form-input" type="password" value={newC.password} onChange={e => setNewC(p => ({...p, password: e.target.value}))} placeholder="Initial password" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Account Code</label>
                  <input className="form-input" value={newC.accountCode} onChange={e => setNewC(p => ({...p, accountCode: e.target.value}))} placeholder="e.g. ACC001" />
                  <div className="form-hint">Used for SBC configuration</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={newC.isAdmin} onChange={e => setNewC(p => ({...p, isAdmin: e.target.checked}))} />
                  Admin account
                </label>
                <button className="btn btn-primary btn-sm" type="submit" disabled={creating} style={{ marginLeft: 'auto' }}>
                  <UserPlus size={13} /> {creating ? 'Creating…' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>

          {/* Edit customer modal */}
          {editCustomer && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,.4)' }}>
                <div style={{ padding: 20, overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: 1, minHeight: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Edit Customer</div>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditCustomer(null)}><X size={13} /> Close</button>
                </div>

                {[
                  ['Name',  'text',     'name',        'Company or user name',    true],
                  ['Email', 'email',    'email',        'user@example.com',        true],
                  ['Password', 'password', 'password', 'Leave blank to keep existing', false],
                  ['Account Code', 'text', 'accountCode', 'e.g. ACC001',          false],
                ].map(([label, type, field, placeholder, required]) => (
                  <div key={field} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                      {label}{required && <span style={{ color: 'var(--err)' }}> *</span>}
                    </div>
                    <input
                      style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-2)', padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                      type={type}
                      value={editDraftC[field] || ''}
                      onChange={e => setEditDraftC(d => ({ ...d, [field]: e.target.value }))}
                      placeholder={placeholder}
                      autoComplete={field === 'password' ? 'new-password' : undefined}
                    />
                  </div>
                ))}

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                    Default Voice Routing Policy
                  </div>
                  <select
                    style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-2)', padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                    value={editDraftC.defaultVrpName || ''}
                    onChange={e => setEditDraftC(d => ({ ...d, defaultVrpName: e.target.value }))}
                  >
                    <option value="">— No default —</option>
                    {customerVrps.map(v => (
                      <option key={v.id} value={v.name}>{v.name}{v.description ? ` — ${v.description}` : ''}</option>
                    ))}
                  </select>
                  {customerVrps.length === 0 && (
                    <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 4 }}>
                      No policies synced for this customer yet — sync via the Teams connector first.
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                    Assigned SBC
                  </div>
                  <select
                    style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-2)', padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', cursor: 'pointer' }}
                    value={editDraftC.sbcId || ''}
                    onChange={e => setEditDraftC(d => ({ ...d, sbcId: e.target.value }))}
                  >
                    <option value="">— No SBC assigned —</option>
                    {sbcsList.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.fqdn ? ` (${s.fqdn})` : ''}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 4 }}>
                    When routing a DID to Teams, the portal will push a dial plan entry to this SBC
                    {editDraftC.sbcId && editDraftC.accountCode && sbcsList.find(s => String(s.id) === String(editDraftC.sbcId))?.fqdn
                      ? <>: <span style={{ color: 'var(--info)', fontFamily: 'monospace' }}>{editDraftC.accountCode}.{sbcsList.find(s => String(s.id) === String(editDraftC.sbcId)).fqdn}</span></>
                      : '.'}
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--muted)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!editDraftC.isAdmin}
                      onChange={e => setEditDraftC(d => ({ ...d, isAdmin: e.target.checked }))}
                      disabled={editCustomer.id === editCustomer?.selfId}
                    />
                    Admin account
                  </label>
                </div>

                {!editDraftC.isAdmin && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontSize: 11, color: 'var(--muted-3)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        Visible modules
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" className="btn btn-secondary btn-xs" onClick={() => setEditModules(CUSTOMER_MODULE_IDS)}>All</button>
                        <button type="button" className="btn btn-secondary btn-xs" onClick={() => setEditModules([])}>None</button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 14px', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                      {CUSTOMER_MODULES.map(m => (
                        <label key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--text-2)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={editModules.includes(m.id)} onChange={() => toggleModule(m.id)} />
                          {m.label}
                        </label>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 4 }}>
                      Controls which items appear in this customer's left-hand menu. Admin accounts always see everything.
                    </div>
                  </div>
                )}

                {/* Per-customer Voxbone routing API override */}
                <div style={{ marginBottom: 20 }}>
                  <button
                    type="button"
                    onClick={() => setVoxOpen(o => !o)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--muted-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', cursor: 'pointer', padding: 0, marginBottom: 8, fontWeight: 600 }}
                  >
                    {voxOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    Voxbone Routing API (optional)
                    {(editVox.hasKey && !editVox.clearKey) && <span style={{ color: 'var(--info)', textTransform: 'none', letterSpacing: 0 }}>— own account set</span>}
                  </button>
                  {voxOpen && (
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 14, lineHeight: 1.5 }}>
                        Reads always use the live read account. This key only affects <strong>writes</strong>: leave blank and this customer's routing changes go to the safe <strong>sandbox</strong> write-default; set a trusted key to push this customer's changes to its own (e.g. live) account.
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>API Base URL</div>
                        <input
                          style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-2)', padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                          type="text"
                          value={editVox.base}
                          placeholder="https://api.voxbone.com"
                          onChange={e => setEditVox(v => ({ ...v, base: e.target.value }))}
                        />
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>Auth Type</div>
                        <select
                          style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-2)', padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', cursor: 'pointer' }}
                          value={editVox.authType}
                          onChange={e => setEditVox(v => ({ ...v, authType: e.target.value }))}
                        >
                          <option value="">— Use system default —</option>
                          {['apikey', 'query', 'query-apikey', 'query-key', 'x-api-key', 'bearer', 'basic', 'basic-token', 'basic-user'].map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <div style={{ fontSize: 11, color: 'var(--muted-3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                          API Key
                          {editVox.hasKey && !editVox.clearKey && <span style={{ color: 'var(--info)', textTransform: 'none', letterSpacing: 0 }}> — key is set</span>}
                        </div>
                        <input
                          style={{ background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-2)', padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                          type="password"
                          autoComplete="new-password"
                          value={editVox.key}
                          disabled={editVox.clearKey}
                          placeholder={editVox.hasKey ? '•••••••• (leave blank to keep existing)' : 'Enter API key'}
                          onChange={e => setEditVox(v => ({ ...v, key: e.target.value }))}
                        />
                        {editVox.hasKey && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-xs"
                            style={{ marginTop: 6 }}
                            onClick={() => setEditVox(v => ({ ...v, clearKey: !v.clearKey, key: '' }))}
                          >
                            {editVox.clearKey ? 'Will remove key on save — undo' : 'Remove custom key'}
                          </button>
                        )}
                      </div>

                      {/* Connection test — runs through the per-customer resolver (read-only). */}
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={voxTest.state === 'testing'}
                          onClick={runVoxTest}
                        >
                          {voxTest.state === 'testing' ? 'Testing…' : 'Test connection'}
                        </button>
                        {(editVox.key.trim() || editVox.clearKey) && (
                          <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6, lineHeight: 1.4 }}>
                            Tests the saved key — Save first to test the key you just entered.
                          </div>
                        )}
                        {voxTest.state === 'ok' && (
                          <div style={{ fontSize: 12, color: 'var(--ok)', marginTop: 8, lineHeight: 1.4 }}>✓ {voxTest.msg}</div>
                        )}
                        {voxTest.state === 'fail' && (
                          <div style={{ fontSize: 12, color: 'var(--err)', marginTop: 8, lineHeight: 1.4 }}>✗ {voxTest.msg}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg)' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditCustomer(null)}>Cancel</button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveCustomer}
                    disabled={savingC || !editDraftC.name?.trim() || !editDraftC.email?.trim()}
                  >
                    {savingC ? 'Saving…' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Customer list */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">All Customers</div>
              <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>{customers.length} accounts</div>
            </div>
            <div className="table-wrap" style={{ overflow: 'visible' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th>Name</th>
                    <th>Primary Email</th>
                    <th>Account Code</th>
                    <th>Role</th>
                    <th>Logins</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map(c => (
                    <>
                      <tr key={c.id}>
                        {/* Expand toggle */}
                        <td style={{ padding: '8px 6px 8px 16px' }}>
                          <button
                            className="btn btn-secondary btn-xs"
                            style={{ padding: '2px 4px', color: expandedId === c.id ? 'var(--brand-text)' : 'var(--muted-3)' }}
                            onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                            title="Manage additional email logins"
                          >
                            {expandedId === c.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </button>
                        </td>
                        <td style={{ fontWeight: 500 }}>
                          <span
                            onClick={() => openEditCustomer(c)}
                            title="Edit customer"
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          >
                            {c.name}
                            <Edit2 size={11} style={{ opacity: 0.4, flexShrink: 0 }} />
                          </span>
                        </td>
                        <td style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'monospace' }}>{c.email}</td>

                        {/* Account code — click to edit inline */}
                        <td>
                          {editingCode?.id === c.id ? (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                              <input
                                value={editingCode.value}
                                onChange={e => setEditingCode({ ...editingCode, value: e.target.value })}
                                onBlur={() => handleSaveCode(c.id, editingCode.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveCode(c.id, editingCode.value);
                                  if (e.key === 'Escape') setEditingCode(null);
                                }}
                                autoFocus
                                style={{ width: 90, fontSize: 12, padding: '2px 6px', background: 'var(--border)', border: '1px solid #3a3f5c', borderRadius: 4, color: 'var(--text-2)', fontFamily: 'monospace' }}
                              />
                              <button className="btn btn-secondary btn-xs" onClick={() => handleSaveCode(c.id, editingCode.value)}>
                                <Check size={10} />
                              </button>
                            </div>
                          ) : (
                            <span
                              onClick={() => setEditingCode({ id: c.id, value: c.account_code || '' })}
                              title="Click to edit"
                              style={{ fontSize: 12, fontFamily: 'monospace', cursor: 'pointer', color: c.account_code ? 'var(--info)' : 'var(--muted-3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                              {c.account_code || <span style={{ color: 'var(--muted-3)' }}>—</span>}
                              <Edit2 size={10} style={{ opacity: 0.5 }} />
                            </span>
                          )}
                        </td>

                        <td>
                          {c.is_admin
                            ? <span className="badge badge-amber">Admin</span>
                            : <span className="badge badge-gray">Customer</span>
                          }
                        </td>
                        <td>
                          <button
                            className="btn btn-secondary btn-xs"
                            style={{ color: expandedId === c.id ? 'var(--brand-text)' : 'var(--muted-2)' }}
                            onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                          >
                            <Mail size={11} />
                            {Number(c.extra_email_count) > 0 && ` +${c.extra_email_count}`}
                          </button>
                        </td>
                        <td style={{ color: 'var(--muted-2)', fontSize: 12 }}>{c.created_at?.slice(0, 10)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="btn btn-secondary btn-xs"
                              onClick={() => openEditCustomer(c)}
                            >
                              <Edit2 size={10} /> Edit
                            </button>
                            <button
                              className="btn btn-secondary btn-xs"
                              style={{ color: 'var(--err)' }}
                              onClick={() => handleDeleteCustomer(c.id, c.name)}
                            >
                              <Trash2 size={11} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded email panel */}
                      {expandedId === c.id && (
                        <tr key={`${c.id}-emails`}>
                          <td colSpan={8} style={{ padding: 0 }}>
                            <EmailsPanel customerId={c.id} onFlash={flash} />
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
